import { Trans, useLingui } from "@lingui/react/macro"
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react"
import {
  PortalPopover,
  PortalSheet,
} from "@/components/v2/chrome/portal-popover"
import { useIsMobile } from "@/hooks/use-is-mobile"
import { initialsColor } from "@/components/v2/assignees/avatar-stack"
import { TeammateRow } from "@/components/v2/settings/phone-numbers/buy/teammate-row"
import { WizardSearch } from "@/components/v2/settings/phone-numbers/buy/wizard-search"

// Rich multi-select "who owns this?" picker shared by
// contacts (assignees) and deals (owners). Desktop: portal
// popover anchored to the trigger; mobile: bottom sheet —
// both portal to document.body. Save applies the optimistic
// set, PATCHes via `onSave`, and reverts to the pre-save
// snapshot when the server answers anything but 204.
//
// Generalized from the contacts reassign picker (CON-139);
// `ReassignContactAssignees` is now a thin wrapper around
// this component.

// One selected person — what the optimistic callback
// receives. Matches `ContactAssignee` / `DealOwner`.
export interface PickerAssignee {
  id: number
  name: string
  initials: string
  color: string
}

// One selectable teammate row. `count` is how many of the
// entity the teammate already owns ("Sales · 3 deals");
// callers map their presenter field (contactsCount /
// dealsCount) onto it.
export interface AssigneePickerRow extends PickerAssignee {
  role: string
  email: string
  count: number
}

export interface AssigneePickerData {
  employees: AssigneePickerRow[]
}

interface AssigneePickerProps {
  open: boolean
  onClose: () => void
  triggerRef: RefObject<HTMLElement | null>
  // "deal" / "contact" — used in the aria-label and the
  // header subtitle copy.
  entityLabel: string
  // The record's display name, shown next to the heading.
  contextName: string
  // Noun for the per-teammate count subtitle ("3 deals").
  countNoun: string
  // Loads the teammate options when the picker opens.
  fetchOptions: (signal?: AbortSignal) => Promise<AssigneePickerData>
  currentAssignees: PickerAssignee[]
  // Applies (and on failure re-applies) the assignee set to
  // the host page's optimistic state.
  applyOptimistic: (assignees: PickerAssignee[]) => void
  // PATCHes the new set. The picker checks
  // `res.status === 204` for success and reverts otherwise.
  onSave: (employeeIds: number[]) => Promise<Response>
  // Prefix for the panel's data-testids — keeps the
  // contacts wrapper's existing ids stable.
  testIdPrefix: string
}

export function AssigneePicker({
  open,
  onClose,
  triggerRef,
  entityLabel,
  contextName,
  countNoun,
  fetchOptions,
  currentAssignees,
  applyOptimistic,
  onSave,
  testIdPrefix,
}: AssigneePickerProps) {
  const isMobile = useIsMobile()

  if (!open) return null

  const panel = (
    <AssigneePickerPanel
      entityLabel={entityLabel}
      contextName={contextName}
      countNoun={countNoun}
      fetchOptions={fetchOptions}
      currentAssignees={currentAssignees}
      onClose={onClose}
      applyOptimistic={applyOptimistic}
      onSave={onSave}
      testIdPrefix={testIdPrefix}
    />
  )

  const ariaLabel = `Who's assigned to this ${entityLabel}?`

  if (isMobile) {
    return (
      <PortalSheet
        open={open}
        onClose={onClose}
        ariaLabel={ariaLabel}
        // Sit above the slide-out drawer (z-50) when the
        // picker is opened from a drawer/slideover
        // (CON-143). Harmless on the index/show — nothing
        // sits above it there.
        zClassName="z-[60]"
      >
        {panel}
      </PortalSheet>
    )
  }

  return (
    <PortalPopover
      open={open}
      onClose={onClose}
      triggerRef={triggerRef}
      placement="bottom-end"
      offset={6}
      ariaLabel={ariaLabel}
      // Sit above the slide-out drawer (z-50) when opened
      // from a drawer/slideover (CON-143).
      zClassName="z-[60]"
      className="w-[340px] rounded-2xl bg-card border border-[#ECE5D8] shadow-[0_16px_40px_rgba(30,27,23,0.18)] overflow-hidden"
    >
      {panel}
    </PortalPopover>
  )
}

function AssigneePickerPanel({
  entityLabel,
  contextName,
  countNoun,
  fetchOptions,
  currentAssignees,
  onClose,
  applyOptimistic,
  onSave,
  testIdPrefix,
}: Omit<AssigneePickerProps, "open" | "triggerRef">) {
  const { t } = useLingui()
  const [pickerData, setPickerData] =
    useState<AssigneePickerData | null>(null)
  const [query, setQuery] = useState("")
  const [employeeIds, setEmployeeIds] = useState<number[]>(
    currentAssignees.map((e) => e.id),
  )
  const [saving, setSaving] = useState(false)
  const reqKey = useRef("")

  // Load the teammate options once the picker opens.
  useEffect(() => {
    const controller = new AbortController()
    const key = String(Date.now())
    reqKey.current = key
    fetchOptions(controller.signal)
      .then((res) => {
        if (reqKey.current === key) setPickerData(res)
      })
      .catch(() => {
        /* leave null → shows loading copy */
      })
    return () => controller.abort()
    // `fetchOptions` is expected to be stable for the
    // lifetime of an open picker — we intentionally load
    // once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filtered = useMemo(() => {
    if (!pickerData) return []
    const q = query.trim().toLowerCase()
    if (!q) return pickerData.employees
    return pickerData.employees.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        e.email.toLowerCase().includes(q) ||
        e.role.toLowerCase().includes(q),
    )
  }, [pickerData, query])

  const selectedCount = employeeIds.length

  function toggleEmployee(id: number) {
    setEmployeeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }

  function save() {
    // Save is disabled until the options load, so `pickerData`
    // is always present here — guard anyway so we never build
    // an empty optimistic set from un-loaded rows.
    if (saving || !pickerData) return
    setSaving(true)

    // Build the optimistic assignee objects from picker data
    // so initials + color are correct. Iterate the rows in
    // presenter (name) order so the optimistic avatar-stack
    // order matches what the server returns on reload.
    const snapshot = currentAssignees
    const selected: PickerAssignee[] = pickerData.employees
      .filter((row) => employeeIds.includes(row.id))
      .map((row) => ({
        id: row.id,
        name: row.name,
        initials: row.initials,
        color: row.color,
      }))

    applyOptimistic(selected)

    onSave(employeeIds)
      .then((res) => {
        if (res.status === 204) {
          onClose()
        } else {
          applyOptimistic(snapshot)
        }
      })
      .catch(() => {
        applyOptimistic(snapshot)
      })
      .finally(() => {
        setSaving(false)
      })
  }

  return (
    <div
      className="flex flex-col max-h-[70vh]"
      data-testid={`${testIdPrefix}-panel`}
    >
      <div className="flex flex-col gap-0.5 px-4 pt-4 pb-2 shrink-0">
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-semibold text-[#1E1B17] text-[15px] leading-5">
            <Trans>Who&apos;s assigned?</Trans>
          </span>
          <span className="text-[#7A746B] text-xs leading-4 shrink-0 truncate max-w-[120px]">
            {contextName}
          </span>
        </div>
        <span className="text-[#7A746B] text-xs leading-4">
          <Trans>Pick the teammates who own this {entityLabel}.</Trans>
        </span>
      </div>

      <div className="px-4 pb-2 shrink-0">
        <WizardSearch
          value={query}
          onChange={setQuery}
          placeholder={t`Search teammates`}
          ariaLabel={t`Search teammates`}
        />
      </div>

      <div className="flex flex-col gap-2 px-4 pb-3 grow overflow-y-auto">
        {pickerData ? (
          <>
            {filtered.map((employee) => (
              <TeammateRow
                key={employee.id}
                testId={`${testIdPrefix}-employee-${employee.id}`}
                selected={employeeIds.includes(employee.id)}
                onSelect={() => toggleEmployee(employee.id)}
                avatar={
                  <span
                    className="flex items-center justify-center size-9 shrink-0 rounded-full"
                    style={{ backgroundColor: employee.color }}
                  >
                    <span
                      className="font-semibold text-xs leading-4"
                      style={{ color: initialsColor(employee.color) }}
                    >
                      {employee.initials}
                    </span>
                  </span>
                }
                title={employee.name}
                subtitle={`${employee.role} · ${employee.count} ${
                  employee.count === 1 ? countNoun : `${countNoun}s`
                }`}
              />
            ))}
            {query.trim() && filtered.length === 0 ? (
              <p className="text-[#7A746B] text-sm leading-5 py-2 text-center">
                {t`No teammates match "${query.trim()}".`}
              </p>
            ) : null}
          </>
        ) : (
          <p className="text-[#7A746B] text-sm leading-5 py-4 text-center">
            <Trans>Loading teammates…</Trans>
          </p>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-[#E5DFD3] px-4 py-3 shrink-0 bg-card pb-[calc(env(safe-area-inset-bottom)+12px)] sm:pb-3">
        <span
          className="text-[#7A746B] text-sm leading-5"
          data-testid={`${testIdPrefix}-count`}
        >
          <Trans>{selectedCount} selected</Trans>
        </span>
        <button
          type="button"
          data-testid={`${testIdPrefix}-save`}
          onClick={save}
          disabled={saving || !pickerData}
          className="inline-flex items-center justify-center rounded-full py-2 px-5 bg-[#1E1B17] text-[#F0EDE6] text-sm leading-5 font-semibold cursor-pointer hover:bg-[#34302A] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
        </button>
      </div>
    </div>
  )
}
